/**
 * Thin bridge that breaks the projectStore ↔ collectionStore circular dependency.
 *
 * collectionStore registers its actions here immediately after the store is created.
 * projectStore reads from here — it never imports collectionStore directly.
 */

type SwitchFn = (id: string) => Promise<void>;
type GetIdFn  = () => string | null;
type SetPartialFn = (partial: { currentCollectionId?: string | null }) => void;

let _switchCollection:     SwitchFn     | null = null;
let _getCollectionId:      GetIdFn      | null = null;
let _setCollectionPartial: SetPartialFn | null = null;

export function registerCollectionRef(opts: {
  switchCollection:  SwitchFn;
  getCollectionId:   GetIdFn;
  setPartialState:   SetPartialFn;
}) {
  _switchCollection     = opts.switchCollection;
  _getCollectionId      = opts.getCollectionId;
  _setCollectionPartial = opts.setPartialState;
}

/** Switch the active collection by ID. No-ops when not authenticated. */
export function switchCollectionById(id: string): Promise<void> {
  if (!_switchCollection) return Promise.resolve();
  return _switchCollection(id);
}

/**
 * Returns the active collection ID.
 * Checks the collection store first, falls back to the projectStore's
 * currentProject.id (set lazily to avoid creating a new import cycle).
 */
export function getActiveCollectionId(): string | null {
  return _getCollectionId?.() ?? null;
}

/** Directly update collectionStore state (e.g. to clear currentCollectionId). */
export function setCollectionPartialState(partial: { currentCollectionId?: string | null }) {
  _setCollectionPartial?.(partial);
}
